import { createSelector } from '@reduxjs/toolkit';
import { Appointment } from './models';
import { baseApi } from '@centerpiece/components';

export const appointment = baseApi
  .enhanceEndpoints({ addTagTypes: ['Appointments', 'Appointment'] })
  .injectEndpoints({
    endpoints: builder => ({
      getAppointments: builder.query<Appointment[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'appointment',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'Appointment' as const,
                  id,
                })),
                'Appointments',
              ]
            : ['Appointments'],
      }),
      getAppointment: builder.query<Appointment, number>({
        query: id => ({
          url: `appointment/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'Appointment', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createAppointment: builder.mutation({
        query: request => ({
          url: 'appointment',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Appointments'],
      }),
      patchAppointment: builder.mutation<Appointment, Appointment>({
        query: request => ({
          url: `appointment/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Appointment', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            appointment.util.updateQueryData('getAppointment', id, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(appointment.util.invalidateTags(['Appointments']))
             */
            dispatch(appointment.util.invalidateTags(['Appointments']));
          }
        },
      }),
      updateAppointment: builder.mutation<Appointment, Appointment>({
        query: request => ({
          url: `appointment/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Appointment', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            appointment.util.updateQueryData('getAppointment', id, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(appointment.util.invalidateTags(['Appointments']))
             */
            dispatch(appointment.util.invalidateTags(['Appointments']));
          }
        },
      }),
      deleteAppointment: builder.mutation<Appointment, number>({
        query: id => ({
          url: `appointment/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Appointment' as const, arg }, 'Appointments']
            : ['Appointments'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetAppointmentsQuery,
  useGetAppointmentQuery,
  useCreateAppointmentMutation,
  usePatchAppointmentMutation,
  useUpdateAppointmentMutation,
  useDeleteAppointmentMutation,
  usePrefetch: useAppointmentsPrefetch,
} = appointment;
const selectAppointmentsRoot = appointment.endpoints.getAppointments.select({});
export const selectAppointments = createSelector(
  selectAppointmentsRoot,
  appointment => appointment.data || []
);
